import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { DepositMethodsQueryKeys } from '../../queryKeys';
import { fetchDepositMethods } from './service';
import { useMemo } from 'react';
import { AxiosError, AxiosInstance } from 'axios';
import {
  useInfiniteQueryReset,
  useInfiniteQueryCallbacks,
  useAxiosInstance,
} from '@starsoft/common/hooks';
import {
  DepositMethod,
  GenericError,
  PaginatedResponse,
} from '@starsoft/common/models';
import { UseDepositMethodsProps, UseDepositMethodsQueryKey } from './props';
import { DepositMethodsInputDto } from '@starsoft/common/dto';

/**
 * Custom hook to fetch and manage deposit methods with infinite scrolling
 *
 * This hook handles fetching deposit methods with pagination support using infinite query,
 * managing the query cache, and providing pagination controls.
 *
 * @param {UseDepositMethodsProps} options - Optional configuration object
 * @param {boolean} [options.enabled=true] - Whether the query should automatically run
 * @param {number} [options.coinId] - ID of the coin to filter deposit methods
 *
 * @returns {Object} An object containing:
 *   - depositMethods: Flattened array of all fetched deposit methods
 *   - isPending: Boolean indicating if initial query is in progress and not idle
 *   - hasNextPage: Boolean indicating if more pages are available
 *   - fetchNextPage: Function to load next page of data
 *   - isFetchingNextPage: Boolean indicating if fetching next page
 *   - hasPreviousPage: Boolean indicating if previous pages exist
 *   - fetchPreviousPage: Function to load previous page
 *   - isFetchingPreviousPage: Boolean indicating if fetching previous page
 *   - refetch: Function to manually refetch all data
 */
export function useDepositMethods(options?: UseDepositMethodsProps) {
  const { enabled = true, coinId } = options ?? {};
  const params: DepositMethodsInputDto = {
    limit: 20,
    coinId,
  };
  const axiosInstance: AxiosInstance = useAxiosInstance();

  const queryKey: UseDepositMethodsQueryKey = [
    DepositMethodsQueryKeys.Primary,
    params,
  ];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();
  useInfiniteQueryReset(queryKey);

  const { data, fetchStatus, isPending, ...infinityQuery } = useInfiniteQuery<
    PaginatedResponse<DepositMethod>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<DepositMethod>>,
    UseDepositMethodsQueryKey
  >({
    queryKey,
    queryFn: queryContext => fetchDepositMethods(queryContext, axiosInstance),
    initialPageParam: 1,
    getNextPageParam,
    staleTime: 5 * 1000,
    getPreviousPageParam,
    refetchOnWindowFocus: false,
    enabled: enabled && !!coinId,
  });

  // Flatten paginated data into a single array of deposit methods
  const formattedData: DepositMethod[] = useMemo(
    () => data?.pages?.map(page => page.data)?.flat() as DepositMethod[],
    [data],
  );

  return {
    ...infinityQuery,
    depositMethods: formattedData,
    isPending: isPending && fetchStatus != 'idle',
  };
}
